import { differenceInYears } from "date-fns";
import { inject } from "mobx-react";
import React, { PropsWithChildren, SyntheticEvent, useCallback, useEffect, useState } from "react";
import type { Store } from "../../../../../../client/stores";
import { AgentOSType } from "../../../../../../client/utils";
import { WindowsIcon } from "../../../../../core/components/icons/WindowsIcon.component";
import { Remote } from "../../../../../core/components/remote/Remote.component";
import { isPWSSessionBackend, PWSSessionState } from "../../../../../core/lib/auth/backends/pws";
import { coastMediaService } from "../../../../../core/services/PWS";
import { Button, ButtonKind, FauxButton } from "../../../../../core/ui";
import { COAST_SHOW_TYPE } from "../../../lib/coastContent";

interface CoastShowAudioboxWMPProps {
    downloadLinks: Record<string, string | null>;
    osType: AgentOSType | null;
    store?: Store;
}

interface CoastShowAudioboxWMPLoaderProps {
    osType: AgentOSType | null;
    showType: COAST_SHOW_TYPE;
    showDate: Date | number;
    showLoading?: boolean;
    store?: Store;
}

interface DownloadLinkProps {
    handleClick: (e: SyntheticEvent) => void;
    url: string | null;
    title?: string;
}

const DownloadLink = ({ children, url, handleClick, title }: PropsWithChildren<DownloadLinkProps>) =>
    url ? (
        <FauxButton title={title} kind={ButtonKind.CUSTOMIZED} className="media-link" download="download" to={url}>
            {children}
        </FauxButton>
    ) : (
        <Button className="media-link" click={handleClick} kind={ButtonKind.CUSTOMIZED}>
            {children}
        </Button>
    );

const CoastShowAudioboxWMP = inject("store")(({ store, osType, downloadLinks }: CoastShowAudioboxWMPProps) => {
    const handleClick = useCallback(
        (e: SyntheticEvent) => {
            e.preventDefault();

            if (osType !== AgentOSType.WINDOWS) {
                return alert("Windows Media Audio is not supported by your device or browser.");
            }

            const session = store?.session.currentSession;

            if (!isPWSSessionBackend(session) || session.state.hasActiveSubscription) {
                return null;
            }

            if (session.state.authenticated) {
                session.showMissingSubscriptionPopup();
            } else {
                session.login();
            }
        },
        [osType, store?.session.currentSession],
    );

    if (!store) {
        return null;
    }

    let high: string | null = null;
    let low: string | null = null;

    Object.entries(downloadLinks).forEach(([type, url]) => {
        if (type.endsWith("low")) {
            low = url;
        } else {
            high = url;
        }
    });

    const title = "Listen with Windows Player";

    return (
        <section className="audiobox-section coast-wmp">
            <div className="audiobox-section-value">
                <div className="link-with-icon">
                    <DownloadLink handleClick={handleClick} url={high} title={title}>
                        <WindowsIcon fill="#fff" />
                    </DownloadLink>

                    <div className="listen-section">
                        <span>{title}</span>

                        <div className="listen-links">
                            <DownloadLink handleClick={handleClick} url={high} title={title}>
                                High
                            </DownloadLink>

                            <DownloadLink handleClick={handleClick} url={low} title={title}>
                                Low
                            </DownloadLink>
                        </div>
                    </div>
                </div>
            </div>
        </section>
    );
});

const wmpLoader = (
    osType: AgentOSType | null,
    showType: COAST_SHOW_TYPE,
    showDate: number | Date,
    session: PWSSessionState,
): Promise<Record<string, string | null>> => {
    const types =
        showType === COAST_SHOW_TYPE.SOMEWHERE_IN_TIME
            ? ["win_somewhereintime_show", "win_somewhereintime_show_low"]
            : ["win_show", "show_low"];

    return osType === AgentOSType.WINDOWS && session.accessToken && session.hasActiveSubscription
        ? coastMediaService.getMPWLinksBulk(types, showDate, session)
        : Promise.resolve({ show: null, low: null });
};

export const CoastShowAudioboxWMPLoader = inject("store")(
    ({ store, showDate, showType, osType, showLoading = true }: CoastShowAudioboxWMPLoaderProps) => {
        const session = store?.session.currentSession;

        if (!store || !isPWSSessionBackend(session)) {
            return null;
        }

        const [hasActiveSubscription, setHasActiveSubscription] = useState(session.state.hasActiveSubscription);

        const handleSessionUpdate = useCallback(
            (state: PWSSessionState) => setHasActiveSubscription(state.hasActiveSubscription),
            [],
        );

        useEffect(() => {
            session.onStatusChanged.subscribe(handleSessionUpdate);
            return () => session.onStatusChanged.unsubscribe(handleSessionUpdate);
        }, [handleSessionUpdate, session.onStatusChanged]);

        // hide mp3 downloads on regular shows that are more than 7 years old
        if (showType === COAST_SHOW_TYPE.REGULAR && differenceInYears(showDate, new Date()) > 7) {
            return null;
        }

        return (
            <Remote
                loader={() => wmpLoader(osType, showType, showDate, session.state)}
                cacheKey={`coast-show-audiobox-wmp-${showType}-${Number(showDate)}-${hasActiveSubscription}`}
                showLoading={showLoading}
            >
                {({ data }) => (data ? <CoastShowAudioboxWMP downloadLinks={data} osType={osType} /> : null)}
            </Remote>
        );
    },
);
